<script lang="ts">
    import type { OcCinemaV1Events } from '@otto-ec/otto-components/cinema';
    import { afterPageLoad } from '@tilelist/components/black-friday-slider/afterPageLoad.browser.ts';
    import ProductTileItem from '@tilelist/components/product-tile-item/ProductTileItem.svelte';
    import type { BlackFridaySliderItem, ProductItem } from '@tilelist/domain/composition/TileListitem.ts';
    import type { TileListPageContext } from '@tilelist/domain/composition/TileListPage.ts';

    type Props = {
        slider: BlackFridaySliderItem;
        context: TileListPageContext;
        cacheRestored?: boolean;
        showRadioEquipmentRegulation?: boolean;
        headline?: string;
        subline?: string;
        onLoad: () => void;
        /*                                                                */
        onVisible: (tiles: ProductItem[]) => void;
        /*                                                              */
        onBrowse: (tiles: ProductItem[]) => void;
        onProductClick: (event: Event, item: ProductItem) => void;
    };

    let {
        slider,
        context,
        cacheRestored = true,
        showRadioEquipmentRegulation = false,
        headline = 'Black Friday Deals',
        subline = 'Die top bewerteten Produkte - bis 1.12 täglich wechselnd',
        onLoad,
        onVisible,
        onBrowse,
        onProductClick,
    }: Props = $props();

    const VISIBLE_RATIO = 0.5;
    const BROWSE_SOURCES: ReadonlySet<string> = new Set(['arrows', 'swipe', 'pagination-dots']);

    let root = $state<HTMLElement | null>(null);
    let loadRequested = false;
    let visibleReported = false;
    let inViewport = false;
    /*                                                                                                   */
    let shownIndices: number[] | undefined;

    /*                                                                                                            */
    $effect(() => {
        if (slider.state !== 'pending') {
            /*                                                                               */
            loadRequested = false;
            return;
        }
        if (!root || loadRequested) return;
        visibleReported = false;
        inViewport = false;
        shownIndices = undefined;
        return afterPageLoad(() => {
            if (loadRequested) return;
            loadRequested = true;
            onLoad();
        });
    });

    /*                                                                                     */
    $effect(() => {
        if (!root || slider.state !== 'ready' || visibleReported) return;
        const observer = new IntersectionObserver(
            (entries) => {
                inViewport = entries.some((entry) => entry.intersectionRatio >= VISIBLE_RATIO);
                reportVisible();
                if (visibleReported) observer.disconnect();
            },
            { threshold: VISIBLE_RATIO },
        );
        observer.observe(root);
        return () => observer.disconnect();
    });

    function tilesAt(indices: number[]): ProductItem[] {
        if (slider.state !== 'ready') return [];
        const { items } = slider;
        return indices.map((index) => items[index]).filter((item): item is ProductItem => !!item);
    }

    function reportVisible() {
        if (visibleReported || !inViewport || !shownIndices) return;
        visibleReported = true;
        onVisible(tilesAt(shownIndices));
    }

    function onVisibilityChanged({ detail }: OcCinemaV1Events['oc-item-visibility-changed']) {
        shownIndices = detail.visibleItems.indices;
        reportVisible();
    }

    function onScrollCompleted({ detail }: OcCinemaV1Events['oc-stage-scroll-completed']) {
        shownIndices = detail.visibleItems.indices;
        if (!detail.source || !BROWSE_SOURCES.has(detail.source)) return;
        onBrowse(tilesAt(detail.visibleItems.indices));
    }
</script>

{#if slider.state !== 'collapsed'}
    <section
        bind:this={root}
        class="reptile-black-friday-slider"
        class:reptile-black-friday-slider--pending={slider.state === 'pending'}
        class:reptile-black-friday-slider--ready={slider.state === 'ready'}
        data-testid="black-friday-slider"
        aria-label={headline}
    >
        {#if slider.state === 'ready'}
            <header class="reptile-black-friday-slider__header">
                <h2 class="reptile-black-friday-slider__headline oc-headline-200">{headline}</h2>
                <p class="reptile-black-friday-slider__subline oc-copy-100">{subline}</p>
            </header>
            <div class="reptile-black-friday-slider__body">
                <oc-cinema-v1
                    oc-aria-label={headline}
                    resizing="none"
                    gap="8px"
                    stage-spacing="0px"
                    onoc-item-visibility-changed={onVisibilityChanged}
                    onoc-stage-scroll-completed={onScrollCompleted}
                >
                    {#each slider.items as item (item.product.id)}
                        <div class="reptile-black-friday-slider__tile">
                            <ProductTileItem
                                {cacheRestored}
                                productItem={item}
                                rule={context.rule}
                                layout={context.layout}
                                loadVariation={async () => {}}
                                onSeen={async () => {}}
                                onTileClick={(event) => onProductClick(event, item)}
                                {showRadioEquipmentRegulation}
                                showTopInfos={false}
                                showInstallments={false}
                                showAvailability={false}
                                showComparison={false}
                                showMultipleSizes={false}
                                searchContext={context.searchContext}
                            />
                        </div>
                    {/each}
                </oc-cinema-v1>
            </div>
        {/if}
    </section>
{/if}

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    $tile-width: 8.75rem;
    $pending-min-height: 30rem;

    .reptile-black-friday-slider {
        grid-column: 1 / -1;

        /*                                                                             */
        &--ready {
            background-color: tokens.$oc-base-color-black-100;
        }

        /*                                                            */
        &--pending {
            min-height: $pending-min-height;
        }

        &__header {
            padding: tokens.$oc-semantic-spacing-absolute-100 tokens.$oc-semantic-spacing-absolute-100
                tokens.$oc-semantic-spacing-absolute-50;
            color: tokens.$oc-semantic-color-text-inverted;
        }

        &__headline,
        &__subline {
            margin: 0;
        }

        &__body {
            padding: tokens.$oc-semantic-spacing-absolute-50;
            background-color: rgb(255 255 255 / 20%);
        }

        &__tile {
            overflow: hidden;

            width: $tile-width;
            border-radius: tokens.$oc-semantic-border-radius-50;

            color: tokens.$oc-semantic-color-text-default;

            background-color: tokens.$oc-base-color-white-100;
        }
    }
</style>
